import React, { useState, useEffect } from "react";
import RegularImg from "../assets/images/regular.png";
import { MdOutlineSendToMobile } from "react-icons/md";
import {
  AiOutlineLoading3Quarters,
  AiOutlineEye,
  AiOutlineEyeInvisible,
} from "react-icons/ai";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import LogoImg from "../../assets/images/Logo.png";
import { config } from "../../config/config.jsx";
import CryptoJS from "crypto-js";
import Swal from "sweetalert2";
import PersonalisationHistoryTable from "./History/PersonalisationHistory.jsx";

function Personalisation() {
  const navigate = useNavigate();

  /* ---------------------------------- data --------------------------------- */
  const cardVerify = [{ label: "TRACKING ID", value: "trackingId" }];

  // Add state for API prices
  const [apiPrices, setApiPrices] = useState(null);
  const [personalisationPrice, setPersonalisationPrice] = useState(0); // Default price

  // Add useEffect to fetch prices when component mounts
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await axios.get(
          `${config.apiBaseUrl}${config.endpoints.currentapipricing}`,
          { withCredentials: true }
        );

        // Find personalisation pricing
        const personalisationPricing = response.data.find(
          (item) => item.key === "personalisation"
        );

        if (personalisationPricing) {
          setPersonalisationPrice(personalisationPricing.prices.agent);
        }
      } catch (error) {
        toast.error("Failed to fetch current prices");
      }
    };

    fetchPrices();
  }, []);

  const cardSlip = [
    {
      label: "Regular Slip",
      value: "regular",
      image: RegularImg,
      price: personalisationPrice,
    },
  ];

  /* ---------------------------- component state ---------------------------- */
  const [formData, setFormData] = useState({
    trackingId: "",
  });
  const [loading, setLoading] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);

  const SECRET_KEY = import.meta.env.VITE_APP_SECRET_KEY;

  function decryptData(ciphertext) {
    if (!ciphertext) return null;
    try {
      const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decrypted);
    } catch {
      return null;
    }
  }

  let userId = null;
  let userPhone = "";
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const userObj = decryptData(userStr);
      userId = userObj?._id || userObj?.id;
      userPhone = userObj?.phone || userObj?.phoneNumber;
    }
  } catch (error) {}

  useEffect(() => {
    if (userPhone) {
      setFormData((prev) => ({ ...prev, phoneNumber: userPhone }));
    }
  }, [userPhone, userId]);

  /* --------------------------------- render -------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.trackingId) {
      toast.error("Please enter a Tracking ID");
      return;
    }

    // Show confirmation dialog
    const result = await Swal.fire({
      title: "Confirm Verification",
      text: `Are you sure you want to proceed with this verification? Amount: ₦${personalisationPrice}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#f59e0b",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, verify",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    setLoading(true);
    const payload = {
      verifyWith: "trackingID",
      slipLayout: "Standard",
      trackingId: formData.trackingId,
      amount: personalisationPrice,
      userId,
      phone: userPhone,
    };

    try {
      const response = await axios.post(
        `${config.apiBaseUrl}${config.endpoints.Personalisation}`,
        payload,
        {
          withCredentials: true,
        }
      );
      setVerificationResult(response.data);

      // Show success alert
      await Swal.fire({
        title: " Successful!",
        text: "Your details has been submitted.",
        icon: "success",
        confirmButtonColor: "#f59e0b",
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="w-full">
      <div className="rounded-2xl mb-10 bg-white p-6 shadow-lg border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Personalisation</h2>
            <p className="text-sm text-gray-500 mt-1">Get your slip printed</p>
          </div>
          <div className="bg-sky-50 px-4 py-2 rounded-lg border border-sky-100 text-sky-700 font-bold text-lg shadow-sm">
            ₦{personalisationPrice.toLocaleString()}
          </div>
        </div>

      <form onSubmit={handleSubmit}>
        <div className="mt-6">

          <div className="flex items-stretch gap-3">
            <input
              type="text"
              className="flex-1 min-w-0 pl-4 py-3 border border-gray-300 focus:border-[#2eca7f] focus:ring-1 focus:ring-[#2eca7f] rounded-lg outline-none transition-all text-gray-700"
              placeholder="Enter NIN/Tracking id and Send"
              required
              name="trackingId"
              value={formData.trackingId || ""}
              onChange={handleInputChange}
              inputMode="text"
              autoComplete="off"
            />
            <button
              type="submit"
              disabled={loading}
              className={`flex-shrink-0 px-6 rounded-lg font-semibold text-white cursor-pointer transition-colors ${
                loading ? "bg-gray-400" : "bg-[#2eca7f] hover:bg-[#25b571]"
              }`}
            >
              {loading ? (
                <AiOutlineLoading3Quarters className="animate-spin text-xl" />
              ) : (
                "Process"
              )}
            </button>
          </div>

          <label className="flex items-start mt-4 space-x-3 cursor-pointer">
            <span className="relative mt-0.5">
              <input
                type="checkbox"
                className="peer shrink-0 appearance-none h-5 w-5 border border-gray-400 rounded-sm bg-white checked:bg-blue-600 checked:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                required
                title="Required"
              />
              <svg
                className="absolute w-4 h-4 text-white left-0.5 top-0.5 pointer-events-none hidden peer-checked:block"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414L8.414 15l-4.121-4.121a1 1 0 011.414-1.414L8.414 12.586l7.879-7.879a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              By checking this box, you agree that the owner of the ID has
              granted you consent to verify his/her identity.
            </span>
          </label>
        </div>
      </form>
      <ToastContainer />
      </div>

      {/* History Table */}
      <div className="w-full mt-10">
        <PersonalisationHistoryTable />
      </div>

    </div>
  );
}

export default Personalisation;
